/**
 * The sittings of the state exam, as the Commission announces them.
 *
 * The calendar is the Commission's: one for the whole country, published a year
 * ahead (2026 on 3 July 2025, 2027 on 14 July 2026) at certyfikatpolski.pl.
 * Nothing in this repository can derive it, so no test can hold it against the
 * truth — what a test can hold is its shape, and `SITTINGS_CHECKED` is the date
 * a person last read it off the Commission's pages. Pages print that date next
 * to the list.
 *
 * Each sitting is a Saturday and the Sunday after, and offers different levels:
 * not every level runs every time (an adult B2 has no sitting between June 2026
 * and February 2027), which is what a candidate planning around a deadline
 * most needs to see. Children and adolescents have their own levels
 * and dates; the app carries the adult papers, so those are listed but not shown.
 *
 * A candidate does not register with the Commission but at a licensed centre,
 * and a centre runs only some of the sittings.
 *
 * When the Commission announces the next year (it has done so in July), add the
 * year's rows here and move `SITTINGS_CHECKED`.
 */
export type SittingLevel = "B1" | "B2" | "C1" | "C2";

export interface Sitting {
  /** Saturday, `YYYY-MM-DD`. */
  start: string;
  /** Sunday, `YYYY-MM-DD`. */
  end: string;
  adults: SittingLevel[];
  /** Children and adolescents. Not shown, kept so the list stays the Commission's. */
  young: SittingLevel[];
}

export const SITTINGS_CHECKED = "2026-09-19";
export const SITTINGS_SOURCE = "https://certyfikatpolski.pl/category/terminy/";

export const SITTINGS: Sitting[] = [
  // 2026 — announced 3 July 2025.
  { start: "2026-02-14", end: "2026-02-15", adults: ["B1", "B2"], young: ["B1", "B2"] },
  { start: "2026-04-25", end: "2026-04-26", adults: ["B1", "B2", "C1"], young: [] },
  { start: "2026-06-27", end: "2026-06-28", adults: ["B1", "B2"], young: [] },
  { start: "2026-10-17", end: "2026-10-18", adults: ["B1", "C1", "C2"], young: [] },
  { start: "2026-12-05", end: "2026-12-06", adults: ["B1"], young: ["B2"] },
  // 2027 — announced 14 July 2026.
  { start: "2027-02-06", end: "2027-02-07", adults: ["B1", "B2"], young: [] },
  { start: "2027-04-10", end: "2027-04-11", adults: ["B1", "B2", "C1"], young: [] },
  { start: "2027-06-12", end: "2027-06-13", adults: ["B1", "B2"], young: ["B1", "B2"] },
  { start: "2027-10-02", end: "2027-10-03", adults: ["B1", "C2"], young: [] },
  { start: "2027-11-27", end: "2027-11-28", adults: ["B1", "C1"], young: ["B2"] },
];

/**
 * The adult sittings that have not finished, soonest first.
 *
 * `today` is a `YYYY-MM-DD` string rather than a `Date`, so the comparison is
 * between two calendar days and no time zone can move a sitting across midnight.
 */
export function upcomingSittings(
  today: string,
  options: { level?: SittingLevel; limit?: number } = {},
): Sitting[] {
  const { level, limit } = options;
  const rows = SITTINGS.filter(
    (s) => s.end >= today && (!level || s.adults.includes(level)),
  ).sort((a, b) => a.start.localeCompare(b.start));
  return limit === undefined ? rows : rows.slice(0, limit);
}

/**
 * The years the upcoming sittings fall in, as a title prints them: "2026–2027",
 * or "2027" once the last sitting of 2026 is over. Computed rather than typed,
 * so a page's title names the year a candidate is searching for without anybody
 * remembering to move it — it follows the rows added each July.
 */
export function sittingYears(today: string): string {
  const years = [...new Set(upcomingSittings(today).map((s) => s.start.slice(0, 4)))];
  if (years.length === 0) return today.slice(0, 4);
  return years.length === 1 ? years[0]! : `${years[0]}–${years[years.length - 1]}`;
}
